import React, { useEffect } from "react";

const BURGUNDY = "#7A1F2E";
const BRASS = "#A87D4A";
const ESPRESSO = "#2A1B14";
const CREAM = "#F2EBE0";
const CREAM_LIGHT = "#FAF5EC";

/**
 * /dapperdan — Private documentary pitch for Dapper Dan Barbershop (Marty).
 * Print-optimised. Not linked anywhere. Shared via direct URL only.
 * Aesthetic: editorial cream + oxblood burgundy + antique brass (Dapper Dan brand DNA).
 */
const DapperDan = () => {
    useEffect(() => {
        document.title = "Dapper Dan — A Documentary Series Pitch";
    }, []);

    return (
        <main
            data-testid="dapperdan-page"
            style={{
                backgroundColor: CREAM,
                color: ESPRESSO,
                fontFamily: "Inter, sans-serif",
                minHeight: "100vh",
                padding: "60px 24px 80px",
            }}
        >
            <style>{`
                @media print {
                    header[data-testid="main-navigation"],
                    a[data-testid="floating-whatsapp-btn"] { display: none !important; }
                    main[data-testid="dapperdan-page"] { padding: 0 !important; background: ${CREAM} !important; }
                    .dd-page { page-break-after: always; }
                    .dd-page:last-child { page-break-after: auto; }
                    body { background: ${CREAM} !important; }
                }
                .dd-page {
                    max-width: 880px;
                    margin: 0 auto 48px;
                    padding: 48px 56px;
                    background: ${CREAM_LIGHT};
                    border: 1px solid rgba(122,31,46,0.18);
                    box-shadow: 0 30px 80px rgba(42,27,20,0.12);
                }
                @media (max-width: 700px) {
                    .dd-page { padding: 28px 22px; }
                }
                .dd-eyebrow {
                    color: ${BURGUNDY};
                    font-size: 10.5px;
                    letter-spacing: 0.34em;
                    text-transform: uppercase;
                    font-weight: 700;
                    margin: 0 0 14px;
                }
                .dd-h1 {
                    font-family: 'Playfair Display', serif;
                    font-weight: 500;
                    font-size: clamp(28px, 4.4vw, 42px);
                    line-height: 1.12;
                    letter-spacing: -0.015em;
                    margin: 0 0 18px;
                    color: ${ESPRESSO};
                }
                .dd-h2 {
                    font-family: 'Playfair Display', serif;
                    font-weight: 500;
                    font-size: 24px;
                    line-height: 1.22;
                    letter-spacing: -0.01em;
                    margin: 0 0 14px;
                    color: ${BURGUNDY};
                }
                .dd-h3 {
                    font-family: 'Inter', sans-serif;
                    font-weight: 700;
                    font-size: 11.5px;
                    letter-spacing: 0.22em;
                    text-transform: uppercase;
                    color: ${BURGUNDY};
                    margin: 24px 0 10px;
                }
                .dd-p {
                    font-size: 14.5px;
                    line-height: 1.78;
                    color: ${ESPRESSO};
                    margin: 0 0 14px;
                    font-weight: 400;
                }
                .dd-p strong { color: ${BURGUNDY}; font-weight: 700; }
                .dd-p em { color: ${BRASS}; font-style: italic; }
                .dd-ul { margin: 0 0 14px; padding: 0 0 0 18px; }
                .dd-ul li {
                    font-size: 14.5px;
                    line-height: 1.75;
                    color: ${ESPRESSO};
                    margin-bottom: 8px;
                    font-weight: 400;
                }
                .dd-ul li strong { color: ${BURGUNDY}; font-weight: 700; }
                .dd-ul li em { color: ${BRASS}; font-style: italic; }
                .dd-divider {
                    height: 1px;
                    background: linear-gradient(90deg, transparent, ${BRASS}, transparent);
                    margin: 32px 0;
                    border: 0;
                    opacity: 0.6;
                }
                .dd-callout {
                    border-left: 3px solid ${BURGUNDY};
                    padding: 12px 18px;
                    margin: 18px 0;
                    background: rgba(122,31,46,0.06);
                    font-size: 14px;
                    line-height: 1.7;
                    color: ${ESPRESSO};
                    font-style: italic;
                    font-weight: 400;
                }
                .dd-callout strong { color: ${BURGUNDY}; font-style: normal; font-weight: 700; }
                .dd-img {
                    width: 100%;
                    display: block;
                    margin: 18px 0;
                    border: 1px solid rgba(122,31,46,0.2);
                }
                .dd-img-cap {
                    color: ${BRASS};
                    font-size: 10.5px;
                    letter-spacing: 0.22em;
                    text-transform: uppercase;
                    text-align: center;
                    margin: -10px 0 24px;
                    font-weight: 600;
                }
                .dd-img-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 14px;
                    margin: 18px 0;
                }
                .dd-img-grid img {
                    width: 100%;
                    aspect-ratio: 4 / 3;
                    object-fit: cover;
                    border: 1px solid rgba(122,31,46,0.2);
                    display: block;
                }
                .dd-table {
                    width: 100%;
                    border-collapse: collapse;
                    margin: 16px 0 8px;
                    font-size: 13px;
                }
                .dd-table th {
                    text-align: left;
                    padding: 10px 12px;
                    background: ${BURGUNDY};
                    color: ${CREAM};
                    font-size: 10.5px;
                    letter-spacing: 0.2em;
                    text-transform: uppercase;
                    font-weight: 700;
                    border-bottom: 1px solid ${BURGUNDY};
                }
                .dd-table td {
                    padding: 12px;
                    border-bottom: 1px solid rgba(122,31,46,0.12);
                    color: ${ESPRESSO};
                    vertical-align: top;
                    line-height: 1.55;
                    font-weight: 400;
                }
                .dd-table tr:nth-child(even) td {
                    background: rgba(168,125,74,0.06);
                }
                .dd-table td:first-child {
                    color: ${BURGUNDY};
                    font-weight: 700;
                    width: 28%;
                }
                .dd-beat {
                    display: grid;
                    grid-template-columns: 100px 1fr;
                    gap: 18px;
                    align-items: start;
                    padding: 14px 0;
                    border-bottom: 1px solid rgba(122,31,46,0.12);
                }
                .dd-beat:last-child { border-bottom: 0; }
                .dd-beat-time {
                    color: ${BURGUNDY};
                    font-family: 'Playfair Display', serif;
                    font-style: italic;
                    font-size: 14px;
                    line-height: 1.1;
                    padding-top: 2px;
                    font-weight: 600;
                }
                .dd-beat-title {
                    color: ${ESPRESSO};
                    font-size: 14px;
                    font-weight: 700;
                    letter-spacing: 0.04em;
                    margin: 0 0 4px;
                }
                .dd-beat-desc {
                    color: ${ESPRESSO};
                    font-size: 13px;
                    line-height: 1.65;
                    margin: 0;
                    font-weight: 400;
                }
                .dd-beat-desc em { color: ${BRASS}; font-style: italic; }
                .dd-beat-desc strong { color: ${BURGUNDY}; font-weight: 700; }
                .dd-sig {
                    font-family: 'Playfair Display', serif;
                    font-style: italic;
                    color: ${BURGUNDY};
                    font-size: 24px;
                    margin: 6px 0 2px;
                    font-weight: 600;
                }
            `}</style>

            {/* ============== COVER ============== */}
            <section className="dd-page" data-testid="dd-cover" style={{
                textAlign: "center",
                padding: "64px 48px",
                background: ESPRESSO,
                color: CREAM,
            }}>
                <img
                    src="/dapperdan/logo.png"
                    alt="Dapper Dan"
                    style={{ width: 280, maxWidth: "70%", margin: "0 auto 28px", display: "block", filter: "brightness(1.1)" }}
                />
                <p className="dd-eyebrow" style={{ color: BRASS }}>A Documentary Series — Pitch Document</p>
                <h1 className="dd-h1" style={{ margin: "0 auto 14px", maxWidth: 620, color: CREAM }}>
                    Dapper Dan.
                </h1>
                <p style={{
                    fontFamily: "Playfair Display, serif",
                    fontStyle: "italic",
                    color: BRASS,
                    fontSize: 22,
                    margin: "0 auto 32px",
                    maxWidth: 540,
                    lineHeight: 1.4,
                    fontWeight: 500,
                }}>
                    The Cut Runs Deep.
                </p>
                <img
                    src="/dapperdan/shop-1.jpg"
                    alt="Dapper Dan shop floor"
                    style={{ width: "100%", aspectRatio: "16 / 9", objectFit: "cover", border: `1px solid ${BRASS}`, margin: "12px 0 32px" }}
                />
                <hr style={{ height: 1, background: `linear-gradient(90deg, transparent, ${BRASS}, transparent)`, border: 0, maxWidth: 360, margin: "32px auto" }}/>
                <p style={{
                    color: CREAM,
                    fontSize: 13,
                    letterSpacing: "0.24em",
                    textTransform: "uppercase",
                    margin: "0 0 6px",
                    fontWeight: 500,
                }}>
                    Filmed, Edited & Scored by Tatva K
                </p>
                <p style={{ color: BRASS, fontSize: 12, margin: "0 0 32px" }}>
                    Dubai · 2026 · A Gift for Marty
                </p>
                <p style={{
                    color: BRASS,
                    fontSize: 10.5,
                    letterSpacing: "0.34em",
                    textTransform: "uppercase",
                    fontWeight: 700,
                    margin: 0,
                }}>
                    Hand-Shot. Hand-Cut. Hand-Scored. No AI.
                </p>
            </section>

            {/* ============== NOTE FROM FILMMAKER ============== */}
            <section className="dd-page" data-testid="dd-letter">
                <p className="dd-eyebrow">A Note from the Filmmaker</p>
                <h2 className="dd-h2" style={{ fontSize: 30, color: ESPRESSO, marginBottom: 24 }}>Marty — read this before the numbers.</h2>
                <p className="dd-p">
                    Bhai, this isn't a pitch. This is a gift. I want to shoot a proper documentary on Dapper Dan — not a promo, not an ad. The shop, the chair, the regulars, the barbers, the banter, the new location coming up. The whole quiet world inside those walls.
                </p>
                <p className="dd-p">
                    I'll shoot it. I'll edit it. I'll score it — original music, no licensing, no AI shortcuts. Both landscape for YouTube and vertical cutdowns for Reels/TikTok. Same shoot, two formats, five platforms.
                </p>
                <p className="dd-p">
                    Bhai, I love you and Pablo bro. That's it.
                </p>
                <p className="dd-p">
                    All I need from you is a <strong>yes</strong>, full access to both shops + the team, and one whiteboard session to scope it. Everything else I handle.
                </p>
                <p className="dd-sig">— Adi</p>
                <p style={{ color: BRASS, fontSize: 12, letterSpacing: "0.16em", textTransform: "uppercase", margin: 0, fontWeight: 600 }}>
                    Director · Editor · Composer
                </p>
            </section>

            {/* ============== 01 THE CONCEPT ============== */}
            <section className="dd-page" data-testid="dd-concept">
                <p className="dd-eyebrow">01 · The Concept</p>
                <h2 className="dd-h1">Miami Ink × Dubai barber craft. Barber-led. Owner in the shadows.</h2>
                <p className="dd-p">
                    <strong>Dapper Dan.</strong> is a 12-episode documentary series following the barbers of Dubai's most distinctive shop — the regulars, the walk-ins, the craft, the day. Not a brand film. Not an ad. A character-driven series anchored on the team holding the clippers.
                </p>
                <p className="dd-p">
                    Martyn, the owner, appears <strong>rarely</strong>. By design. He's the quiet hand behind the shop — private by nature, allergic to PR. When he does appear on camera, it's because the stakes have moved. The shop knows. The series treats his presence like weather: felt, brief, consequential.
                </p>
                <p className="dd-p">
                    The chair is the most honest interview room ever invented. Someone holding clippers to your head gets stories nobody else gets. We point a camera at that, and the series tells itself.
                </p>
                <div className="dd-callout">
                    Title: <strong>Dapper Dan. The Cut Runs Deep.</strong> Every episode ends with the closing card. Every confessional, every fade, every walk-out earns it.
                </div>
                <h3 className="dd-h3">Reference DNA</h3>
                <ul className="dd-ul">
                    <li><strong>Miami Ink / LA Ink</strong> (TLC) — the format. Crew, clients, confessionals, shop drama.</li>
                    <li><strong>Chef's Table</strong> (Netflix) — the look. Patient, cinematic, sound-first.</li>
                    <li><strong>The Last Dance</strong> (ESPN/Netflix) — the B-plot pacing (new shop opening as season-long arc).</li>
                    <li><strong>The Bear</strong> (Hulu) — the tension. The shop floor under pressure.</li>
                </ul>
            </section>

            {/* ============== 02 WHY DAPPER DAN ============== */}
            <section className="dd-page" data-testid="dd-why">
                <p className="dd-eyebrow">02 · Why Dapper Dan</p>
                <h2 className="dd-h1">Right shop. Right cast. Right moment.</h2>
                <div className="dd-img-grid">
                    <img src="/dapperdan/shop-2.jpg" alt="Dapper Dan interior" />
                    <img src="/dapperdan/shop-3.jpg" alt="Dapper Dan chair" />
                </div>
                <p className="dd-p">
                    Dapper Dan is the rarest kind of business — a place where the brand, the craft, and the personalities are already a story. The shop is dressed for camera. The cast is built in. The new location is the arc. Nothing has to be manufactured.
                </p>
                <h3 className="dd-h3">What the shop already brings to camera</h3>
                <ul className="dd-ul">
                    <li><strong>10,000+ precision styles</strong> delivered since launch — a real volume of regulars with real stories.</li>
                    <li><strong>500+ happy clients</strong> — a deep bench of recurring characters to weave through episodes.</li>
                    <li><strong>10+ years of combined experience</strong> across the barber team.</li>
                    <li><strong>Est. 2025</strong> — the brand is young, the trajectory is fast. We document the rise.</li>
                    <li><strong>A second Dapper Dan opening</strong> — the season's narrative spine, end to end.</li>
                    <li><strong>Five-star reviews mentioning barbers by name</strong> (Ian, Jason and the team) — the cast is already publicly cast.</li>
                </ul>
                <div className="dd-callout">
                    Marty's instinct built the shop. The series documents the instinct — and what it costs.
                </div>
            </section>

            {/* ============== 03 CAST OF CHARACTERS ============== */}
            <section className="dd-page" data-testid="dd-cast">
                <p className="dd-eyebrow">03 · Cast of Characters</p>
                <h2 className="dd-h1">Master Barber. Veteran. Young Gun. Wildcard. Gatekeeper. The Quiet Owner.</h2>
                <p className="dd-p">
                    Six on-camera characters carry Season 1 — five on the floor, one in the shadows. Names locked in scoping. Energy described below.
                </p>
                <table className="dd-table">
                    <thead>
                        <tr>
                            <th>Character</th>
                            <th>Role</th>
                            <th>On-Camera Energy</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>The Master Barber</td>
                            <td>The Artist · Anchor of the Show</td>
                            <td>The senior craftsman. Runs the floor. Decides the standard. Quiet precision or loud personality — either way, the camera loves him. The series' anchor presence. The "Chris Núñez" of the shop. <strong>Heart of the show.</strong></td>
                        </tr>
                        <tr>
                            <td>The Veteran</td>
                            <td>The Trusted Hand</td>
                            <td>Older, trusted, handles the regulars. Holds the lineage of "the old way" against the shop's new energy. Quiet wisdom. Long pauses. Heavy weight when he speaks.</td>
                        </tr>
                        <tr>
                            <td>The Young Gun</td>
                            <td>The Up-and-Comer</td>
                            <td>Fresh energy. TikTok native. Pushes boundaries. Friction with the Veteran. Grows on camera, episode by episode.</td>
                        </tr>
                        <tr>
                            <td>The Wildcard</td>
                            <td>The Shop Culture Anchor</td>
                            <td>The comedian. The roaster. The one who breaks tension with one line. Could be a female barber, could be the loudest one. Either way — chemistry gold.</td>
                        </tr>
                        <tr>
                            <td>The Gatekeeper</td>
                            <td>Receptionist / Manager</td>
                            <td>Handles bookings, knows everyone's drama, sees every client walk in and out. The eyes and ears of the shop. The most surprising voice in the series.</td>
                        </tr>
                        <tr>
                            <td>Martyn</td>
                            <td>The Quiet Owner · Special Presence</td>
                            <td>Private. Allergic to PR. Built the shop, runs the operation from the shadows. Features rarely — by design. When he appears on camera, the energy shifts. The shop knows something is at stake. <strong>Treated like weather: felt, brief, consequential.</strong></td>
                        </tr>
                    </tbody>
                </table>
            </section>

            {/* ============== 04 THE QUIET OWNER ============== */}
            <section className="dd-page" data-testid="dd-martyn">
                <p className="dd-eyebrow">04 · The Quiet Owner</p>
                <h2 className="dd-h1">Marty appears rarely — and that's the point.</h2>
                <p className="dd-p">
                    Martyn does not anchor the show. By design. He doesn't want to. The series respects that absolutely — and uses it.
                </p>
                <p className="dd-p">
                    Marty features in roughly <strong>four of the twelve episodes</strong>. When he does, it's never small. It's because the stakes have moved — a problem the team can't solve, an opening, a private milestone, the finale. His scarcity is the texture. Less Marty makes more Marty.
                </p>
                <h3 className="dd-h3">When Marty appears on camera</h3>
                <table className="dd-table">
                    <thead>
                        <tr><th>Trigger</th><th>What the Camera Catches</th></tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>A Closed-Door Decision</td>
                            <td>Something material is on the table — a hire, a fire, an opening, a brand call. Marty walks in. Says little. Decides. Leaves.</td>
                        </tr>
                        <tr>
                            <td>The Build-Out</td>
                            <td>The second Dapper Dan coming together. Marty on site between meetings. Walls, mirrors, layout. The shop's future in his head, not on a deck.</td>
                        </tr>
                        <tr>
                            <td>A Private Moment</td>
                            <td>Off-floor. A coffee with the Master Barber. A quiet aside with the Gatekeeper. The kind of conversation only the camera being there once captures.</td>
                        </tr>
                        <tr>
                            <td>The Finale</td>
                            <td>One sit-down. One portrait. Why he built it. What it cost. What it's for. The quietest, longest, most intimate scene of the season.</td>
                        </tr>
                    </tbody>
                </table>
                <div className="dd-callout">
                    No talking-head Marty in cold opens. No Marty VO over montages. No Marty in every mirror reveal. The shop runs the shop — Marty runs the world the shop sits inside.
                </div>
            </section>

            {/* ============== 05 EPISODE BEAT SHEET ============== */}
            <section className="dd-page" data-testid="dd-beats">
                <p className="dd-eyebrow">05 · Episode Beat Sheet</p>
                <h2 className="dd-h1">17 minutes. Six acts. Same skeleton every week.</h2>
                <p className="dd-p">
                    Each episode follows the same structure. Familiar enough to binge. Variable enough to surprise. The client story and the shop drama change weekly — the spine doesn't.
                </p>

                <div className="dd-beat">
                    <span className="dd-beat-time">0:00 – 1:30</span>
                    <div>
                        <p className="dd-beat-title">A · Cold Open</p>
                        <p className="dd-beat-desc">A client walks in. The Gatekeeper greets them from the desk. The Master Barber looks up from his station, nods. <em>"You're with me today."</em> Or someone else, depending. The shop is in motion before we've explained anything. · Vertical clip hook: a single line from the chair that earns the next 17 minutes.</p>
                    </div>
                </div>

                <div className="dd-beat">
                    <span className="dd-beat-time">1:30 – 4:00</span>
                    <div>
                        <p className="dd-beat-title">B · The Shop Intro</p>
                        <p className="dd-beat-desc">Opening montage: clippers, steam, banter, the floor in rhythm. Brief VO from whichever barber leads the episode. Shop drama lands quietly: a delivery short, a no-show, a clipper packing up. The barbers handle it — they always have.</p>
                    </div>
                </div>

                <div className="dd-beat">
                    <span className="dd-beat-time">4:00 – 8:00</span>
                    <div>
                        <p className="dd-beat-title">C · The Confessional / Client Story</p>
                        <p className="dd-beat-desc">Client in the chair. Cape on. The barber asks the real question: <em>"So what's the real reason you're here?"</em> Client opens up. The barber listens, hands moving, eyes on the work. The room narrows to two people.</p>
                    </div>
                </div>

                <div className="dd-beat">
                    <span className="dd-beat-time">8:00 – 14:00</span>
                    <div>
                        <p className="dd-beat-title">D · The Cut</p>
                        <p className="dd-beat-desc">Process montage. Hands. Clipper close-ups. Scissor work. Razor lines. Cross-cut with shop banter, the Wildcard roasting the Young Gun, the Veteran working in silence, the Gatekeeper fielding calls. The client's confessional continues underneath. <strong>Vertical gold:</strong> a single beat of the craft — clipper drop, razor wipe, fade meeting skin — held for one second longer than feels right.</p>
                    </div>
                </div>

                <div className="dd-beat">
                    <span className="dd-beat-time">14:00 – 17:00</span>
                    <div>
                        <p className="dd-beat-title">E · The Mirror / The Reveal</p>
                        <p className="dd-beat-desc">Cape comes off. Client stands. The barber holds the mirror behind their head. <em>"What do you think?"</em> Client reacts. The shop pauses for a half-second. The barber's face says the rest.</p>
                    </div>
                </div>

                <div className="dd-beat">
                    <span className="dd-beat-time">17:00 – End</span>
                    <div>
                        <p className="dd-beat-title">F · The Shop Close</p>
                        <p className="dd-beat-desc">Client pays at the desk. The Gatekeeper books them in again. Banter resumes. Next client walks in. Closing shot: the shop at rhythm. End card: <strong>Dapper Dan. The Cut Runs Deep.</strong></p>
                    </div>
                </div>
            </section>

            {/* ============== 06 SHOP DRAMA PILLARS ============== */}
            <section className="dd-page" data-testid="dd-drama">
                <p className="dd-eyebrow">06 · Shop Drama Pillars</p>
                <h2 className="dd-h1">Seven recurring conflict engines. The shop solves them — Marty steps in only when he must.</h2>
                <p className="dd-p">
                    Each episode draws shop drama from one (or more) of these seven pillars. They're real, recurring, and run by the team on the floor. Marty appears in two or three of them across the season — when the stakes leave the floor's hands.
                </p>
                <table className="dd-table">
                    <thead>
                        <tr><th>Pillar</th><th>The Drama</th></tr>
                    </thead>
                    <tbody>
                        <tr><td>The Staff Problem</td><td>A barber threatens to quit. The team navigates it. Marty only walks in if it reaches him.</td></tr>
                        <tr><td>The Money</td><td>Rent is due. A supplier wants paid. The Gatekeeper carries the weight — and feels it.</td></tr>
                        <tr><td>The Expansion</td><td>The second Dapper Dan is coming. The team feels it shift the room. <strong>Marty appears.</strong></td></tr>
                        <tr><td>The Brand</td><td>A bad review online. The barbers read it together. Quietly absorbed.</td></tr>
                        <tr><td>The Inspection</td><td>Health / code inspection day. The shop scrubs. The Master Barber leads.</td></tr>
                        <tr><td>The Collab</td><td>A product brand wants in. The team weighs it. <strong>Marty appears</strong> to decide.</td></tr>
                        <tr><td>The Regular</td><td>Client only wants one barber. He's sick. Someone has to sell the backup.</td></tr>
                    </tbody>
                </table>
            </section>

            {/* ============== 07 SEASON 1 - 12 EPISODES ============== */}
            <section className="dd-page" data-testid="dd-season">
                <p className="dd-eyebrow">07 · Season 1 — 12 Episodes</p>
                <h2 className="dd-h1">Twelve client stories. Twelve shop dramas. One arc.</h2>
                <p className="dd-p">
                    Each episode pairs one <strong>client archetype</strong> with one <strong>shop drama</strong>. The expansion of the second Dapper Dan runs as the season-long B-plot. Marty appears in roughly four episodes — marked below with a quiet brass mark.
                </p>
                <table className="dd-table">
                    <thead>
                        <tr><th style={{width: "8%"}}>Ep</th><th style={{width: "22%"}}>Client Archetype</th><th>Title</th><th>Shop Drama</th></tr>
                    </thead>
                    <tbody>
                        <tr><td>01</td><td>The Comeback</td><td>"Six Months of Growth, Six Years of Pain"</td><td>A new barber starts. The team has to absorb him.</td></tr>
                        <tr><td>02</td><td>The Executive</td><td>"The CEO Who Forgot His Own Face"</td><td>A pricing dispute on the floor. The Veteran holds the line.</td></tr>
                        <tr><td>03</td><td>The Kid</td><td>"First Cut, New Man"</td><td>Shop floods overnight. The team opens anyway.</td></tr>
                        <tr><td>04</td><td>The Wedding</td><td>"I Need to Look Like I Deserve Her"</td><td>A product collab arrives. <em>Marty appears</em> to weigh it.</td></tr>
                        <tr><td>05</td><td>The Fix</td><td>"They Called It a Fade. It Was a Crime."</td><td>The Master Barber rescues another shop's disaster.</td></tr>
                        <tr><td>06</td><td>The Regular</td><td>"Every Two Weeks for Ten Years"</td><td>Shop anniversary. The Gatekeeper plans the night.</td></tr>
                        <tr><td>07</td><td>The Breakup</td><td>"She Took Everything. I'm Taking My Hair Back."</td><td>Staff tension boils. A barber threatens to walk.</td></tr>
                        <tr><td>08</td><td>The Beard</td><td>"The Beard That Hid a Man"</td><td>A clipper packs up mid-shift. The team improvises.</td></tr>
                        <tr><td>09</td><td>The Collab</td><td>"Celebrity Cut: [Name] Walks In"</td><td>The shop floor goes viral overnight. The team holds.</td></tr>
                        <tr><td>10</td><td>The Father</td><td>"My Son's First Cut. My Father's Last."</td><td>The most intimate episode. The chair holds two generations.</td></tr>
                        <tr><td>11</td><td>The Disaster</td><td>"The No-Show That Broke the Day"</td><td>Everything goes wrong. The team holds it together.</td></tr>
                        <tr><td>12</td><td>The Legacy</td><td>"Marty: A Quiet Portrait"</td><td>Season finale. <em>Marty appears</em> for one long sit-down. The crew. The shop. The new doors open.</td></tr>
                    </tbody>
                </table>
            </section>

            {/* ============== 08 VISUAL & SONIC LANGUAGE ============== */}
            <section className="dd-page" data-testid="dd-language">
                <p className="dd-eyebrow">08 · Visual & Sonic Language</p>
                <h2 className="dd-h1">Warm tungsten. Mirror reflections. Original score. Patient cuts.</h2>
                <div className="dd-img-grid">
                    <img src="/dapperdan/shop-4.jpg" alt="Shop floor light" />
                    <img src="/dapperdan/shop-5.jpg" alt="Barber chair detail" />
                </div>
                <h3 className="dd-h3">Look</h3>
                <ul className="dd-ul">
                    <li><strong>Warm tungsten</strong> base — the shop's existing lighting carries 70% of the mood.</li>
                    <li><strong>Low-key, shallow depth of field.</strong> Chairs hero-lit. Backgrounds soft.</li>
                    <li><strong>Mirrors used heavily.</strong> Reflections everywhere — they tell two stories at once.</li>
                    <li><strong>Close-ups obsessive:</strong> hands, clippers, razors, foam, hair on the floor, coffee cups, the mirror.</li>
                    <li><strong>Patient wides:</strong> let the shop breathe. Resist the Reels-brain edit pace.</li>
                </ul>
                <h3 className="dd-h3">Sound</h3>
                <ul className="dd-ul">
                    <li><strong>Original score</strong> — composed and produced by Tatva K. No music licensing, no clearance headaches, no AI loops. The series will have its own sonic identity.</li>
                    <li><strong>Sparse, soulful, slightly cinematic.</strong> Think <em>Chef's Table</em> tonal palette with a Manila-rooted soul layer.</li>
                    <li><strong>Diegetic sound is sacred:</strong> the clipper hum, the razor scrape, the chair squeak, the shop banter. We mix that loud and proud.</li>
                    <li><strong>Confessionals:</strong> single camera, locked-off, 50mm, shop interior, low light, barber chair as the seat.</li>
                </ul>
            </section>

            {/* ============== 09 CROSS-PUBLISHING ============== */}
            <section className="dd-page" data-testid="dd-formats">
                <p className="dd-eyebrow">09 · Two-Format Cross-Publishing</p>
                <h2 className="dd-h1">One shoot. Two formats. Five platforms.</h2>
                <p className="dd-p">
                    Every episode is shot to deliver in both landscape and vertical, harvested from the same footage. The vertical assets drive traffic to the long-form. No additional production cost, massive reach multiplier.
                </p>
                <table className="dd-table">
                    <thead>
                        <tr><th>Asset</th><th>Format</th><th>Platform</th><th>Length</th></tr>
                    </thead>
                    <tbody>
                        <tr><td>Episode master</td><td>Landscape 16:9</td><td>YouTube · Vimeo</td><td>~17 min</td></tr>
                        <tr><td>Episode trailer</td><td>Landscape 16:9</td><td>YouTube · Web</td><td>60–90 sec</td></tr>
                        <tr><td>Reels cutdowns</td><td>Vertical 9:16</td><td>IG · TikTok · Shorts</td><td>30–60 sec × 4–6 per ep</td></tr>
                        <tr><td>Stills</td><td>4:5 + 9:16</td><td>IG grid + Stories</td><td>6–10 per ep</td></tr>
                        <tr><td>Score EP</td><td>Audio</td><td>Spotify · Apple Music</td><td>Full season soundtrack</td></tr>
                    </tbody>
                </table>
            </section>

            {/* ============== 10 PRODUCTION STACK ============== */}
            <section className="dd-page" data-testid="dd-stack">
                <p className="dd-eyebrow">10 · Production Stack</p>
                <h2 className="dd-h1">Lean. Founder-shot. Broadcast-grade output.</h2>
                <ul className="dd-ul">
                    <li><strong>Camera A:</strong> Tatva's primary mirrorless body — main coverage.</li>
                    <li><strong>Camera B:</strong> Second body or GoPro — locked-off chair wide for cutaways.</li>
                    <li><strong>Audio:</strong> 2× wireless lavs (Rode Wireless GO II) for Martyn + the active barber. Shotgun on a boom for ambient shop sound — the barber chatter <em>is</em> the show.</li>
                    <li><strong>Lights:</strong> One practical key + one bounce. We lean into the shop's existing tungsten — only sweetening, never replacing.</li>
                    <li><strong>Edit:</strong> DaVinci Resolve (broadcast-grade colour + sound, free).</li>
                    <li><strong>Score:</strong> Tatva's home studio. Original, evergreen, owned outright.</li>
                    <li><strong>Crew:</strong> Just Tatva, plus one second-hand for audio + B-cam when needed.</li>
                </ul>
                <div className="dd-callout">
                    The lean stack is the point. Bigger crews kill shop intimacy. The smaller the camera footprint, the more honest the footage.
                </div>
            </section>

            {/* ============== 11 TIMELINE ============== */}
            <section className="dd-page" data-testid="dd-timeline">
                <p className="dd-eyebrow">11 · Timeline</p>
                <h2 className="dd-h1">17 weeks. Scope to release.</h2>
                <table className="dd-table">
                    <thead>
                        <tr><th>Phase</th><th>Weeks</th><th>What Happens</th></tr>
                    </thead>
                    <tbody>
                        <tr><td>Scoping</td><td>Week 1</td><td>Whiteboard session with Marty + barbers. Permissions, releases, shot lists, episode arcs agreed.</td></tr>
                        <tr><td>Principal Shoot</td><td>Weeks 2–8</td><td>One full shooting day per week per shop. Plus event shoots for the expansion build-out. Roughly 80–100 hours of raw footage.</td></tr>
                        <tr><td>Edit + Score + Colour</td><td>Weeks 9–14</td><td>All 12 episodes cut. Original score composed in parallel. Colour graded. Sound mixed.</td></tr>
                        <tr><td>Trailer Drop</td><td>Week 15</td><td>Trailer launch + Reels teaser week.</td></tr>
                        <tr><td>Episode Release</td><td>Weeks 16–27</td><td>One episode per Thursday at 8pm UAE time for 12 weeks. Reels cutdowns spaced between.</td></tr>
                    </tbody>
                </table>
            </section>

            {/* ============== 12 DISTRIBUTION ============== */}
            <section className="dd-page" data-testid="dd-distribution">
                <p className="dd-eyebrow">12 · Release & Distribution Strategy</p>
                <h2 className="dd-h1">Slow burn. Compound algorithm. Land the finale on the new shop opening.</h2>
                <ul className="dd-ul">
                    <li><strong>Day -7 from Ep 1:</strong> First Reels cutdown drops quietly. No announcement.</li>
                    <li><strong>Day -3:</strong> Full trailer drops on IG + YouTube + WhatsApp broadcast to regulars.</li>
                    <li><strong>Day 0 (Thursday, 8pm UAE):</strong> Episode 1 goes live.</li>
                    <li><strong>One episode every Thursday for 12 weeks.</strong></li>
                    <li><strong>3–4 Reels cutdowns per episode</strong>, spaced through the following week — keeps the algorithm hot.</li>
                    <li><strong>Final episode lands the week the new Dapper Dan opens.</strong> The series IS the marketing for shop #2.</li>
                </ul>
                <h3 className="dd-h3">Festival & long-tail</h3>
                <ul className="dd-ul">
                    <li>Eligible for <strong>Dubai International Film Festival</strong>, <strong>Vimeo Staff Picks</strong>, <strong>NY Doc</strong>, <strong>SXSW shorts circuit</strong> — free PR pipelines.</li>
                    <li>Evergreen YouTube watch time → compounds for years.</li>
                    <li>Score EP on streaming → ongoing brand presence in a category no barbershop has touched.</li>
                </ul>
            </section>

            {/* ============== 13 WHAT THIS BECOMES ============== */}
            <section className="dd-page" data-testid="dd-outcomes">
                <p className="dd-eyebrow">13 · What This Becomes for Dapper Dan</p>
                <h2 className="dd-h1">A brand asset Dapper Dan owns forever.</h2>
                <div className="dd-img-grid">
                    <img src="/dapperdan/shop-6.jpg" alt="Shop floor" />
                    <img src="/dapperdan/shop-7.jpg" alt="Mirror detail" />
                </div>
                <ul className="dd-ul">
                    <li><strong>The best brand asset Dapper Dan will ever own.</strong> Evergreen, ungated, distribution-friendly.</li>
                    <li><strong>Doubles as opening-week PR for shop #2.</strong> The doc lands in tandem with the new location.</li>
                    <li><strong>A reference reel</strong> Marty can hand to any future investor in any future business.</li>
                    <li><strong>Festival + award circuit eligible.</strong> Free press, on the house.</li>
                    <li><strong>An audience pipeline</strong> for the brand — IG, TikTok, YouTube, Spotify (via the original score) — all compounding.</li>
                    <li><strong>A defensible brand moat.</strong> When someone else opens a barbershop in Dubai, Dapper Dan has a series. They don't.</li>
                </ul>
                <div className="dd-callout">
                    Most barbershops sell haircuts. After this series, Dapper Dan sells a world.
                </div>
            </section>

            {/* ============== 14 THE ASK ============== */}
            <section className="dd-page" data-testid="dd-ask">
                <p className="dd-eyebrow">14 · The Ask</p>
                <h2 className="dd-h1">Three things.</h2>
                <p className="dd-p">
                    This is a gift, bhai. Genuinely. If down the line you want to put the series on the shop's IG and tag the brand, beautiful. We can structure anything whenever it makes sense.
                </p>
                <p className="dd-p">
                    For now, three things only:
                </p>
                <h3 className="dd-h3">1 · A yes</h3>
                <p className="dd-p">A green light from you. That's the whole permission layer. Nothing happens without it.</p>

                <h3 className="dd-h3">2 · Full access</h3>
                <p className="dd-p">Both shops. The team. The back office. The supplier calls. The bad days. The good ones. No staging — just the work as it is.</p>

                <h3 className="dd-h3">3 · One scoping evening</h3>
                <p className="dd-p">90 minutes to lock the cast, the schedule, the no-go topics, and the release dates. Then I disappear into pre-production and you go back to running your shop.</p>
            </section>

            {/* ============== 15 ABOUT THE FILMMAKER ============== */}
            <section className="dd-page" data-testid="dd-filmmaker">
                <p className="dd-eyebrow">15 · About the Filmmaker</p>
                <h2 className="dd-h1">Tatva K — director, editor, composer.</h2>
                <p className="dd-p">
                    Tatva is the rare combination this series needs: a working recording artist with 150M+ aggregate streams, a 13-year radio + production background, and a hand-built filmmaking practice. The whole series — direction, edit, score — sits inside one operator.
                </p>
                <h3 className="dd-h3">Music & audio</h3>
                <ul className="dd-ul">
                    <li><strong>150M+ aggregate streams</strong> across catalogue (including major chart hits).</li>
                    <li><strong>13-year radio + production background</strong> — broadcast-grade audio craft.</li>
                    <li><strong>TEDx Speaker.</strong> Featured by <strong>BBC</strong> and <strong>Rolling Stone</strong>.</li>
                    <li><strong>Featured Practitioner on Insight Timer</strong> — the world's largest meditation & recovery platform.</li>
                </ul>
                <h3 className="dd-h3">Filmmaking</h3>
                <ul className="dd-ul">
                    <li>Self-shot, self-edited <strong>The Reset Sanctuary</strong> LP videos + the vertical content library powering its funnels.</li>
                    <li>Operator's eye for story economy — Halbert-trained direct-response background means every cut earns its place.</li>
                    <li>Original score, in-house. No licensing. No AI.</li>
                </ul>
                <div className="dd-callout">
                    "Hand-produced. Human. No AI." — the operating principle behind every Tatva K release, audio or visual.
                </div>
            </section>

            {/* ============== CONTACT ============== */}
            <section className="dd-page" data-testid="dd-contact" style={{ textAlign: "center" }}>
                <img
                    src="/dapperdan/logo.png"
                    alt="Dapper Dan"
                    style={{ width: 220, maxWidth: "60%", margin: "0 auto 24px", display: "block" }}
                />
                <hr className="dd-divider" style={{ maxWidth: 280, margin: "32px auto" }}/>
                <p style={{
                    color: BURGUNDY,
                    fontSize: 11,
                    letterSpacing: "0.34em",
                    textTransform: "uppercase",
                    fontWeight: 700,
                    margin: "0 0 8px",
                }}>
                    Dapper Dan. The Cut Runs Deep.
                </p>
                <p style={{ color: BRASS, fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", margin: 0, fontWeight: 600 }}>
                    A Gift, From Adi — For Marty
                </p>
            </section>
        </main>
    );
};

export default DapperDan;
